import { useState } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { useAppSelector } from '@app/hooks';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import {
  useListRiskNotificationsQuery,
  useMarkRiskNotificationSeenMutation,
  useRespondToRiskDispatchMutation,
} from '@shared/api';

/**
 * Глобальный in-app попап «Риски договора для вашего отдела». Монтируется в
 * AppShell рядом с UpdatesPopup. Юридическая служба разослала риски договора по
 * отделам; сотрудник отдела видит центрированную карточку с кратким описанием и
 * кнопкой «Скачать документ», а также может сразу отправить обратную связь
 * юристам (принято / учтено в работе / есть возражения).
 */

const Body = styled.div`
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Meta = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FeedbackBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const FeedbackLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ErrorText = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const Actions = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`;

export function RiskDispatchPopup() {
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const { data } = useListRiskNotificationsQuery(undefined, { skip: !accessToken });
  const [markSeen] = useMarkRiskNotificationSeenMutation();
  const [respondRisk, respondState] = useRespondToRiskDispatchMutation();
  const [dismissed, setDismissed] = useState<string[]>(() => {
    try {
      const raw = window.sessionStorage.getItem('osnova:risk_dispatch_dismissed');
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  const [downloading, setDownloading] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState<'accepted' | 'mitigated' | 'disputed'>(
    'accepted',
  );
  const [feedbackComment, setFeedbackComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const items = data?.items ?? [];
  const current = items.find(
    (n) => n.seen_at == null && !dismissed.includes(n.target_id),
  );

  if (!current) return null;
  const cur = current;

  function close() {
    setError(null);
    setDismissed((prev) => {
      const next = prev.includes(cur.target_id) ? prev : [...prev, cur.target_id];
      try {
        window.sessionStorage.setItem('osnova:risk_dispatch_dismissed', JSON.stringify(next));
      } catch {
        /* ignore storage quota */
      }
      return next;
    });
  }

  async function onSendFeedback() {
    setError(null);
    try {
      await respondRisk({
        targetId: cur.target_id,
        status: feedbackStatus,
        comment: feedbackComment.trim() || undefined,
      }).unwrap();
      setFeedbackComment('');
      try {
        await markSeen(cur.target_id).unwrap();
      } catch {
        /* no-op: лента перечитается по WS/refetch */
      }
      close();
    } catch {
      setError('Не удалось сохранить ответ отдела. Попробуйте ещё раз.');
    }
  }

  async function onDownload() {
    setDownloading(true);
    setError(null);
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
    const res = await downloadAuthorizedFile(
      `${base}/agents/statut/risk-dispatch-targets/${cur.target_id}/download`,
      { accessToken, fallbackName: cur.filename },
    );
    setDownloading(false);
    if (!res.ok) {
      setError(`Не удалось скачать документ (${res.error ?? res.status}).`);
      return;
    }
  }

  const dept = cur.department ?? 'вашего отдела';

  return (
    <Modal open title="Риски договора для вашего отдела" onClose={close}>
      <Body>
        Юридическая служба направила перечень правовых рисков по договору с{' '}
        <strong>{cur.counterparty}</strong>
        {cur.subject ? ` («${cur.subject}»)` : ''} для проверки в части компетенции
        отдела «{dept}».
      </Body>
      <Meta>
        {cur.focus_count > 0 ? (
          <>
            Рисков вашего отдела: <strong>{cur.focus_count}</strong> (всего по договору{' '}
            {cur.risk_count}). В документе они выделены в первом разделе — просмотрите и
            направьте заключение в юридическую службу.
          </>
        ) : (
          <>
            Всего рисков по договору: <strong>{cur.risk_count}</strong>. Ознакомьтесь и
            сообщите, если что-то относится к вашему отделу.
          </>
        )}
      </Meta>
      <FeedbackBox>
        <FeedbackLabel>Заключение отдела для юридической службы</FeedbackLabel>
        <Select
          value={feedbackStatus}
          onChange={(e) =>
            setFeedbackStatus(e.target.value as 'accepted' | 'mitigated' | 'disputed')
          }
          aria-label="Решение отдела по рискам"
        >
          <option value="accepted">Принято к сведению</option>
          <option value="mitigated">Учтено в работе (риски сняты)</option>
          <option value="disputed">Есть возражения / требуются правки договора</option>
        </Select>
        <Input
          value={feedbackComment}
          onChange={(e) => setFeedbackComment(e.target.value)}
          placeholder="Комментарий отдела (необязательно)"
        />
      </FeedbackBox>
      {error && <ErrorText role="alert">{error}</ErrorText>}
      <Actions>
        <Button type="button" $variant="ghost" onClick={close}>
          Позже
        </Button>
        <Button type="button" $variant="secondary" onClick={onDownload} disabled={downloading}>
          {downloading ? 'Скачивание…' : 'Скачать документ'}
        </Button>
        <Button type="button" onClick={onSendFeedback} disabled={respondState.isLoading}>
          {respondState.isLoading ? 'Отправка…' : 'Направить заключение'}
        </Button>
      </Actions>
    </Modal>
  );
}
